import { canUseDOM } from 'exenv';
import { t } from 'i18next';
import moment from 'moment-timezone';
import qs from 'qs';
import { SUPPORTED_DATE_FORMATS } from '../../common/dates';
import { SITE_LANGUAGES_BY_I18CODE } from '../../common/localeShared';
import { TCallback, TDict, TRealmResource, TRegionResource, TSubRegionResource } from '../../types/utilityTypes';
import _ from 'lodash';
import { asset } from '../services/assetResolverService';
import { ChangeEvent } from 'react';
import Numeral from 'numeral';

export function buildUrl(hostPath: string, queryParams: Object) {
  const queryString = qs.stringify(queryParams, { encodeValuesOnly: true });
  if (queryString) {
    return `${hostPath}?${queryString}`;
  }
  return `${hostPath}`;
}

export function checkGetResult<T>(result: any, callback: TCallback<T>) {
  if (result.isBoom) {
    callback(new Error('Something went wrong'));
  } else {
    callback(null, result);
  }
}

export function factionClass(faction: string) {
  return `rio-faction-color--${faction.toLowerCase()}`;
}

// moment.relativeTimeThreshold('d', 50);
// moment.relativeTimeRounding((val) => val);

let defaultTimezone: string;
let defaultTimezoneUTCOffset: number;
let currentLocale: string;

// ensure we initialize all the timezone data
setDefaultTimezone(moment.tz.guess());

let defaultTimeFormatWithSeconds = 'HH:mm:ss';
let defaultTimeFormat = 'HH:mm';
let defaultIs24Hour = true;
let defaultDayMonthYearFormat = 'M/DD/YYYY';
let defaultNaturalLongDateFormat = 'MMM Do YYYY';
let defaultNaturalShortDayMonthYearFormat = 'MMM D YYYY';
let defaultNaturalShortDayMonthFormat = 'MMM D';

let DATE_FMT_NATURAL = '';
let DATE_FMT_NATURAL_SHORT = '';
let DATE_FMT_NUMERIC_SHORT = '';
let DATE_ONLY_FMT = '';
let DATE_FMT_NATURAL_NO_YEAR = '';
let DATE_MONTH_AND_DAY_ONLY_NATURAL = '';
let DATE_FMT_NATURAL_SHORT_NOTIME = '';

const DEFAULT_ISO_CODE = 'en-us';

function refreshTimeFormats() {
  DATE_FMT_NATURAL = `${defaultNaturalLongDateFormat} ${defaultTimeFormatWithSeconds} z`;
  DATE_FMT_NATURAL_SHORT = `${defaultNaturalShortDayMonthYearFormat} ${defaultTimeFormat} z`;
  DATE_FMT_NUMERIC_SHORT = `${defaultDayMonthYearFormat} ${defaultTimeFormat} z`;
  DATE_ONLY_FMT = defaultDayMonthYearFormat;
  DATE_FMT_NATURAL_NO_YEAR = `${defaultNaturalShortDayMonthFormat} ${defaultTimeFormat} z`;
  DATE_MONTH_AND_DAY_ONLY_NATURAL = defaultNaturalShortDayMonthFormat;
  DATE_FMT_NATURAL_SHORT_NOTIME = defaultNaturalShortDayMonthYearFormat;
}

refreshTimeFormats();

export function setDefaultDateFormat(formatLabel: string) {
  const fmt = SUPPORTED_DATE_FORMATS.find((f) => f.label === formatLabel);
  if (!fmt) {
    return;
  }
  defaultDayMonthYearFormat = fmt.dayMonthYearFormat;
  defaultNaturalLongDateFormat = fmt.naturalLongDateFormat;
  defaultNaturalShortDayMonthFormat = fmt.naturalShortDayMonthFormat;
  defaultNaturalShortDayMonthYearFormat = fmt.naturalShortDayMonthYearFormat;
  refreshTimeFormats();
}

export const setCurrentLocale = (locale: string) => {
  currentLocale = locale;
};

export function setDefaultTimezone(timezone: string) {
  defaultTimezone = timezone;
  defaultTimezoneUTCOffset = timezoneOffset(timezone);
}

export const getCurrentLocale = () => {
  return currentLocale;
};

export function getDefaultTimezone() {
  return defaultTimezone;
}

export function getDefaultTimezoneUTCOffset() {
  return defaultTimezoneUTCOffset;
}

export function getDefaultIs24Hour() {
  return defaultIs24Hour;
}

export function getDefaultDateFormat() {
  return defaultDayMonthYearFormat;
}

export function getDefaultTimeFormat() {
  return defaultTimeFormat;
}

export function setDefault24HourClock(is24HourClock: boolean) {
  if (is24HourClock) {
    defaultTimeFormatWithSeconds = 'HH:mm:ss';
    defaultTimeFormat = 'HH:mm';
    defaultIs24Hour = true;
  } else {
    defaultTimeFormatWithSeconds = 'h:mm:ss A';
    defaultTimeFormat = 'h:mm A';
    defaultIs24Hour = false;
  }
  refreshTimeFormats();
}

export function timezoneOffset(str: string | undefined) {
  return moment.tz(str || 'UTC').utcOffset();
}

export function offsetToUtc(str: string | undefined) {
  // to go to UTC, you need to subtract the UTC Offset
  return moment.tz(str || 'UTC').utcOffset() * -1;
}

export function offsetToTimezone(str: string | undefined) {
  return moment.tz(str || 'UTC').utcOffset();
}

export function momentForTimezone(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone);
}

export function naturalDate(str: string, tz = true) {
  const m = moment.utc(str) as any;

  if (!tz) {
    return m.format(DATE_FMT_NATURAL);
  }

  return m.tz(defaultTimezone).format(DATE_FMT_NATURAL);
}

export function naturalShortDate(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(DATE_FMT_NATURAL_SHORT);
}

export function naturalShortDateNoYear(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(DATE_FMT_NATURAL_NO_YEAR);
}

export function naturalMonthAndDay(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(DATE_MONTH_AND_DAY_ONLY_NATURAL);
}

export function naturalMonthAndDayAndWeek(str: string): string {
  const m = moment.utc(str).tz(defaultTimezone) as any;
  const monthDay = m.format(DATE_MONTH_AND_DAY_ONLY_NATURAL);
  return `${monthDay} • ${m.format('dddd')}`;
}

export function naturalMonthAndDayAndYear(str: string, withOrdinal?: boolean) {
  const m = moment.utc(str) as any;
  let format = DATE_FMT_NATURAL_SHORT_NOTIME;
  if (withOrdinal) {
    format = format.replace('D', 'Do');
  }
  return m.tz(defaultTimezone).format(format);
}

function relativeDateWithMode(mode: 'short' | 'long', now: moment.Moment, fromDate: moment.Moment) {
  const m = moment.duration(now.diff(fromDate));

  const { weeks, days, hours, minutes } = getTimePartsWithWeeks(m.asMilliseconds());

  for (const { unit, value } of [
    { unit: 'weeks', value: weeks },
    { unit: 'days', value: days },
    { unit: 'hours', value: hours },
    { unit: 'minutes', value: minutes }
  ]) {
    if (value > 0) {
      return t(`common:durations.${mode}.${unit}`, { count: value });
    }
  }

  return t(`common:durations.${mode}.just-now`);
}

export function relativeDate(str: string) {
  const now = moment.utc();
  const requestedDate = moment.utc(str);
  const durationInMinutes = moment.duration(requestedDate.diff(now)).asMinutes();

  if (Math.abs(durationInMinutes) < 1) {
    if (now < requestedDate) {
      return t('common:durations.long.just-now-future');
    } else {
      return t('common:durations.long.just-now');
    }
  }

  const duration = relativeDateWithMode('long', now, requestedDate);
  if (now < requestedDate) {
    return t('common:durations.relative-future-date', { duration });
  } else {
    return t('common:durations.relative-past-date', { duration });
  }
}

export function relativeDateShort(str: string) {
  const now = moment.utc();
  const requestedDate = moment.utc(str);
  return relativeDateWithMode('short', now, requestedDate);
}

export function relativeDateIfRecent(str: string, hours: number = 24, showYear: boolean = false) {
  const now = moment.utc();
  const m = moment.utc(str);
  const diff = moment.duration(now.diff(m)).asHours();
  if (diff > hours) {
    return showYear ? naturalShortDate(str) : naturalShortDateNoYear(str);
  }
  return relativeDateWithMode('long', now, m);
}

export function shortDate(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(DATE_FMT_NUMERIC_SHORT);
}

export function shortTime(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(defaultTimeFormat);
}

export function shortTimeWithTimezone(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(defaultTimeFormat + ' z');
}

export function dateOnly(str: string) {
  const m = moment.utc(str) as any;
  return m.tz(defaultTimezone).format(DATE_ONLY_FMT);
}

export function formatMs(ms: number) {
  const duration = moment.duration(ms);
  let seconds = Math.floor(duration.asSeconds());
  const days = Math.floor(seconds / (3600 * 24));
  seconds -= days * (3600 * 24);
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;

  const units = [
    ['day', days],
    ['h', hours],
    ['m', minutes],
    ['s', seconds]
  ];
  return units
    .filter(([, count]) => Number(count) > 0)
    .map(([unit, count]: any) => {
      if (unit === 'day') {
        return `${count} ${plural(unit, count)}`;
      } else {
        return `${count}${unit}`;
      }
    })
    .join(' ');
}

export function formatMsNoSeconds(ms: number) {
  const { days, hours, minutes } = getTimeParts(ms);
  const parts: string[] = [];
  if (days > 0) {
    parts.push(t('common:durations.short.days', { count: days }));
  }
  if (hours > 0) {
    parts.push(t('common:durations.short.hours', { count: hours }));
  }
  if (minutes > 0) {
    parts.push(t('common:durations.short.minutes', { count: minutes }));
  }
  return parts.join(' ');
}

export function formatMsLong(ms: number) {
  const duration = moment.duration(ms);
  let seconds = Math.floor(duration.asSeconds());
  const days = Math.floor(seconds / (3600 * 24));
  seconds -= days * (3600 * 24);
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;

  const units = [
    ['day', days],
    ['hour', hours],
    ['minute', minutes],
    ['second', seconds]
  ];
  return units
    .filter(([, count]) => Number(count) > 0)
    .map(([unit, count]: any) => {
      return `${count} ${plural(unit, count)}`;
    })
    .join(' ');
}

export function zeroPad(n: number, padSize: number = 2): string {
  let str = n.toString();
  while (str.length < padSize) {
    str = '0' + str;
  }
  return str;
}

export function calcTotalMinutesForRange(start: number, end: number) {
  let totalMinutes = 0;
  if (start < end) {
    totalMinutes = end - start;
  } else {
    totalMinutes = 60 * 24 - start + end;
  }
  return totalMinutes;
}

export function formatMinutes(totalMinutes: number, is24Hour: boolean = defaultIs24Hour) {
  const { hours, minutes, suffix } = getMinutesParts(totalMinutes, is24Hour);
  return `${zeroPad(hours, 2)}:${zeroPad(minutes, 2)}${suffix}`;
}

export function getMinutesParts(totalMinutes: number, is24Hour: boolean = defaultIs24Hour) {
  let hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  let suffix = '';

  if (!is24Hour) {
    suffix = ' AM';
    if (hours >= 12) {
      hours -= 12;
      suffix = ' PM';

      // use PM suffix up until new day at 12:00 AM
      if (hours === 12) {
        suffix = ' AM';
      }
    }

    if (hours === 0) {
      hours = 12;
    }
  }

  return {
    hours,
    minutes,
    suffix
  };
}

export function formatTimeRange(startTime: number, endTime: number, is24Hour: boolean = defaultIs24Hour) {
  const start = getMinutesParts(startTime);
  const end = getMinutesParts(endTime);

  if (isNaN(start.hours) || isNaN(end.hours)) {
    return '';
  }

  if (startTime === endTime) {
    return t('recruitment:schedule.any-time');
  }

  const parts: string[] = [];

  // for 12-hour time we don't pad the hours
  const padHours = is24Hour ? zeroPad : (n: number) => n.toString();

  const padMinutes = zeroPad;

  parts.push(padHours(start.hours));
  const startMinutes = padMinutes(start.minutes);
  if (startMinutes) {
    parts.push(':', startMinutes);
  }

  if (!is24Hour) {
    parts.push(start.suffix);
  }

  parts.push(' - ');

  if (is24Hour && end.hours === 0 && end.minutes === 0) {
    parts.push('24:00');
  } else {
    parts.push(padHours(end.hours));

    const endMinutes = padMinutes(end.minutes);
    if (endMinutes) {
      parts.push(':', endMinutes);
    }

    parts.push(end.suffix);
  }

  return parts.join('');
}

export function getTimeParts(ms: number) {
  const duration = moment.duration(ms);
  let seconds = Math.floor(duration.asSeconds());
  // const months = Math.floor(seconds / (3600 * 24 * 30));
  // seconds -= months * (3600 * 24 * 30);
  const days = Math.floor(seconds / (3600 * 24));
  seconds -= days * (3600 * 24);
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;

  const fractionalSeconds = duration.asSeconds() - seconds;

  return { days, hours, minutes, seconds, fractionalSeconds };
}

export function getTimePartsWithWeeks(ms: number) {
  const duration = moment.duration(ms);
  let seconds = Math.abs(Math.floor(duration.asSeconds()));

  const weeks = Math.floor(seconds / (3600 * 24 * 7));
  seconds -= weeks * (3600 * 24 * 7);
  const days = Math.floor(seconds / (3600 * 24));
  seconds -= days * (3600 * 24);
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;

  const fractionalSeconds = duration.asSeconds() - seconds;

  return { weeks, days, hours, minutes, seconds, fractionalSeconds };
}

export function formatMsShort(ms: number, precision: number = 0) {
  const { days, hours, minutes, seconds, fractionalSeconds } = getTimeParts(ms);

  let timePart = [zeroPad(hours), zeroPad(minutes), zeroPad(seconds)].join(':');

  if (precision > 0) {
    timePart += '.' + fractionalSeconds.toFixed(precision).split('.')[1];
  }

  if (days > 0) {
    return `${days} ${plural('day', days)} ${timePart}`;
  }

  return timePart;
}

export function formatMsShortNoSeconds(ms: number) {
  const { days, hours, minutes } = getTimeParts(ms);

  let timePart = [zeroPad(hours), zeroPad(minutes)].join(':');

  if (days > 0) {
    return `${days} ${plural('day', days)} ${timePart}`;
  }

  return timePart;
}

export function formatCountdownMsTime(ms: number) {
  const { days, hours, minutes, seconds } = getTimeParts(ms);

  if (days > 0) {
    return t('common:countdowns.dhms', { days, hours, minutes, seconds });
  }

  return t('common:countdowns.hms', { hours, minutes, seconds });
}

export function formatRoundedBiggestUnit(ms: number) {
  const { days, hours, minutes, seconds, fractionalSeconds } = getTimeParts(ms);
  if (days > 0) {
    const roundedDays = Math.round(days + hours / 24);
    return t('common:durations.long.days', { count: roundedDays });
  } else if (hours > 0) {
    const roundedHours = Math.round(hours + minutes / 60);
    return t('common:durations.long.hours', { count: roundedHours });
  } else if (minutes > 0) {
    const roundedMinutes = Math.round(minutes + seconds / 60);
    return t('common:durations.long.minutes', { count: roundedMinutes });
  } else {
    const roundedSeconds = Math.round(seconds + fractionalSeconds / 1.0);
    return t('common:durations.long.seconds', { count: roundedSeconds });
  }
}

export function formatMsShortNoHours(ms: number, precision: number = 0, padAll: boolean = false) {
  const { days, hours, minutes, seconds, fractionalSeconds } = getTimeParts(ms);

  const timeParts: string[] = [];
  if (hours !== 0) {
    timeParts.push(padAll ? zeroPad(hours) : hours.toString());
    timeParts.push(zeroPad(minutes));
  } else {
    timeParts.push(padAll ? zeroPad(minutes) : minutes.toString());
  }

  timeParts.push(zeroPad(seconds));

  let timePart = timeParts.join(':');

  if (precision > 0) {
    timePart += '.' + fractionalSeconds.toFixed(precision).split('.')[1];
  }

  if (days > 0) {
    return `${days} ${plural('day', days)} ${timePart}`;
  }

  return timePart;
}

export function formatMsFull(ms: number) {
  const duration = moment.duration(ms);
  let milliseconds = Math.floor(duration.asMilliseconds());
  const days = Math.floor(milliseconds / (3600 * 24 * 1000));
  milliseconds -= days * (3600 * 24 * 1000);
  const hours = Math.floor(milliseconds / (3600 * 1000));
  milliseconds -= hours * 3600 * 1000;
  const minutes = Math.floor(milliseconds / (60 * 1000));
  milliseconds -= minutes * 60 * 1000;
  const seconds = Math.floor(milliseconds / 1000);
  milliseconds -= seconds * 1000;

  const timePart = [zeroPad(hours), zeroPad(minutes), zeroPad(seconds)].join(':') + '.' + zeroPad(milliseconds, 3);

  if (days > 0) {
    return `${days} ${plural('day', days)} ${timePart}`;
  }

  return timePart;
}

export function formatMsFullNoHours(ms: number) {
  const duration = moment.duration(ms);
  let milliseconds = Math.floor(duration.asMilliseconds());
  const minutes = Math.floor(milliseconds / (60 * 1000));
  milliseconds -= minutes * 60 * 1000;
  const seconds = Math.floor(milliseconds / 1000);
  milliseconds -= seconds * 1000;

  const timePart = [minutes, zeroPad(seconds)].join(':') + '.' + zeroPad(milliseconds, 3);

  return timePart;
}

export function plural(str: string, num: number) {
  if (num === 1) {
    return str;
  }
  return `${str}s`;
}

export function numberWithSuffix(num: number): string {
  return t('common:ordinal', { count: num, ordinal: true });
}

export const ResponsiveTerms: any = {
  mythicPlus: ['common:terms.mplus', 'common:terms.mplus-abbreviation']
};

export function areFieldsDifferent(fields: string[], obj1: TDict<any>, obj2: TDict<any>) {
  if (!obj1 || !obj2) {
    return false;
  }

  for (const f of fields) {
    if (typeof obj1[f] !== typeof obj2[f]) {
      return true;
    }

    if (typeof obj1[f] === 'string') {
      // case insensitive compare on strings
      if (obj1[f].toLowerCase() !== obj2[f].toLowerCase()) {
        return true;
      }
    } else {
      if (obj1[f] !== obj2[f]) {
        return true;
      }
    }
  }

  return false;
}

/**
 * realm resource to appropriate slug
 */
export function buildRealmSlug(rsrc: TRealmResource, withoutConnected?: boolean) {
  if (rsrc.isConnected && !withoutConnected) {
    return `connected-${rsrc.slug}`;
  }
  return rsrc.slug;
}

export function dictToHashKeys(dict: any) {
  const parts: string[] = [];
  for (const key of Object.keys(dict)) {
    parts.push(`${key}=${dict[key]}`);
  }
  return parts.join(':');
}

export function getHashVars() {
  if (!canUseDOM) {
    return {};
  }

  const hash = window.location.hash;
  if (hash) {
    const keyPairs = hash.substr(1).split(':');
    const newFilters: any = {};
    for (const kp of keyPairs) {
      const [key, val] = kp.split('=', 2);
      if (val === undefined) {
        continue;
      }
      newFilters[key] = val;
    }
    return newFilters;
  }

  return {};
}

export const getHashVarsByString = (hash: string) => {
  if (!canUseDOM) {
    return {};
  }

  const keyPairs = hash.substr(1).split(':');
  const newFilters: any = {};
  for (const kp of keyPairs) {
    const [key, val] = kp.split('=', 2);
    if (val === undefined) {
      continue;
    }
    newFilters[key] = val;
  }

  return newFilters;
};

export function hexToRgb(hex: string) {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = parseInt('0x' + c.join(''), 16);
    return [(c >> 16) & 255, (c >> 8) & 255, c & 255];
  }
  return [0, 0, 0];
}

export function formatWowheadComparisonItem(item: any) {
  return [item.item_id, item.bonuses && item.bonuses.length > 0 ? item.bonuses[0] : 0, item.enchant || 0]
    .join('.')
    .replace(/(\.0)+$/g, '');
}

export function sortByName(a: any, b: any) {
  if (a.name === b.name) {
    return 0;
  } else if (a.name < b.name) {
    return -1;
  } else {
    return 1;
  }
}

export function sortByShortName(a: any, b: any) {
  if (a.short_name === b.short_name) {
    return 0;
  } else if (a.short_name < b.short_name) {
    return -1;
  }
  return 1;
}

export function findBySlug(list: any[], needle: any) {
  const foundMatches = list.filter((item) => item.slug === needle);
  if (!foundMatches || foundMatches.length === 0) {
    return list[0];
  }
  return foundMatches[0];
}

export function trackLinkingToVideos(type: string, itemId?: string) {
  RIO_TRACK_EVENT('event', 'select_content', {
    content_type: `click_${type}`,
    ...(itemId ? { content_id: itemId } : {})
  });
}

export function findBySlugWithNull(list: any[], needle: any) {
  const foundMatches = list.filter((item) => item.slug === needle);
  if (!foundMatches || foundMatches.length === 0) {
    return null;
  }
  return foundMatches[0];
}

/**
 * This function is used to return a formatted string based on an integer with localization
 * by using Intl.NumberFormat (Docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat)
 * It will use the current locale by default, but optionally can accept another locale. Defaults to american english if the locale is not supported.
 * @param num the number to be formatted.
 * @param locale optional param if you want to use a locale other than the currently selected one. This should be a code like 'en' not its ISO code.
 * @returns String that represents a localized number.
 */
export const formatInt = (num: number, locale?: string): string => {
  const ISO_CODE = SITE_LANGUAGES_BY_I18CODE[locale || currentLocale]?.localeCode || DEFAULT_ISO_CODE;
  return new Intl.NumberFormat(ISO_CODE, { minimumFractionDigits: 0 }).format(num);
};

/**
 * This function is used to return a formatted string based on an integer with localization
 * by using Intl.NumberFormat (Docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat)
 * It will use the current locale by default, but optionally can accept another locale. *note* not recommended for Currency.
 * @param num the number to be formatted. Defaults to american english if the locale is not supported.
 * @param precision optionally the number of decimals to force. Defaults to 2.
 * @param locale optional param if you want to use a locale other than the currently selected one. This should be a code like 'en' not its ISO code.
 * @returns String that represents a localized number.
 */
export const formatFloat = (num: number, precision?: number, locale?: string): string => {
  const DEFAULT_PRECISION = 2;
  const ISO_CODE = SITE_LANGUAGES_BY_I18CODE[locale || currentLocale]?.localeCode || DEFAULT_ISO_CODE;
  return new Intl.NumberFormat(ISO_CODE, { minimumFractionDigits: precision || DEFAULT_PRECISION }).format(num);
};

/*
 * Returns the string representation of a role derived from the numeric spec ID.
 * This requires groupedSpecs to be passed, which comes from the specsByRoleSelector
 * in selectors/global
 */
export const getRoleFromSpecId = _.memoize(
  (id: number, groupedSpecs: any): string => {
    if (_.find(groupedSpecs.tank, { id })) {
      return 'tank';
    }
    if (_.find(groupedSpecs.healer, { id })) {
      return 'healer';
    }
    if (_.find(groupedSpecs['dps-melee'], { id })) {
      return 'dps-melee';
    }
    if (_.find(groupedSpecs['dps-ranged'], { id })) {
      return 'dps-ranged';
    }
    throw new Error(`Unrecognized spec ID ${id}`);
  },
  (specId: number, groupedSpecs: any): string => `${specId}_${JSON.stringify(groupedSpecs)}`
);

export const eventHandlerUtils = {
  createEventTargetValueHandler:
    <T extends HTMLInputElement | HTMLSelectElement>(handlerFunction: (value: string) => void) =>
    (e: ChangeEvent<T>) => {
      handlerFunction(e.target.value);
    },
  createEventTargetCheckedHandler:
    <T extends HTMLInputElement>(handlerFunction: (value: boolean) => void) =>
    (e: ChangeEvent<T>) => {
      handlerFunction(e.target.checked);
    },
  stopPropagation: (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  }
};

export function secondsToMinutesSeconds(seconds: number | null) {
  if (seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }
  return null;
}

export const buildBackgroundStyle = (urls: (string | null | undefined)[]) => {
  return urls
    .filter(Boolean)
    .map((url) => `url(${url})`)
    .join(', ');
};

export const parseSeparatedString = <T>(str: string, mapper: (str: string) => T, separator = ','): T[] => {
  if (!str) {
    return [];
  }

  return str.split(separator).flatMap((s) => {
    const result = mapper(s);

    if (result === undefined) {
      return [];
    }

    return [result];
  });
};

export const formatTz = (tz: string) => {
  const formatted = moment.tz(tz).format('z');
  if (formatted !== tz) {
    return `${tz} (${formatted})`;
  } else {
    return tz;
  }
};

export function encodeSearchParams(params: any) {
  return qs.stringify(params, {
    encodeValuesOnly: true,
    allowDots: false
  });
}

export function decodeSearchParams(str: string, params?: any) {
  return qs.parse(str, {
    allowDots: false,
    ...(params ? params : {})
  });
}

// NOTE: Got the SVG flags from here: https://flagicons.lipis.dev/
export const getIconUrlForRegion = (region: TRegionResource, subRegion: TSubRegionResource | undefined) => {
  const slug = subRegion?.short_name.toLowerCase() || region.slug;
  return asset(`/images/region-flags/${slug}.svg`);
};

/**
 *
 * @param percentage 0-1
 * @returns
 */
export const formatPercentage = (percentage: number, decimalPlaces: number = 2) => {
  const fractional = decimalPlaces > 0 ? `.${'0'.repeat(decimalPlaces)}` : '';
  return Numeral(percentage).format(`0${fractional}%`);
};

export const cleanObject = <T extends object>(obj: T, omitValues: unknown[] = [null, undefined]): T => {
  return _.pickBy(obj, (value) => !omitValues.includes(value)) as T;
};
